// ✅ src/pages/VehicleV2/VehicleBreakdownReport.jsx
// Production listing template

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleBreakdownReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const exportToCsv = (rows, filename = "vehicle-idle-breakdown-report.csv") => {
  if (!rows?.length) return;
  const safe = rows.map((r) => {
    const c = { ...r };
    delete c._row;
    return c;
  });
  const headers = Object.keys(safe[0] || {});
  const esc = (val) => {
    const s = String(val ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [headers.join(","), ...safe.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const monthOptions = ["Select","January","February","March","April","May","June","July","August","September","October","November","December"];
const buildYearOptions = () => {
  const now = new Date().getFullYear();
  const yrs = ["Select"];
  for (let y = now; y >= now - 25; y--) yrs.push(String(y));
  return yrs;
};

const buildFilterParams = (filters) => {
  const params = new URLSearchParams();
  params.set("vehicleBasis", filters.vehicleBasis || "OWN");
  params.set("month", filters.month || "Select");
  params.set("year", filters.year || "Select");
  params.set("branchId", filters.branchId || "Select");
  params.set("regNo", filters.regNo || "");
  params.set("vehicleCode", filters.vehicleCode || "");
  params.set("vehicleName", filters.vehicleName || "");
  params.set("fromDate", filters.fromDate || "");
  params.set("toDate", filters.toDate || "");
  params.set("vehicleTypeId", filters.vehicleTypeId || "Select");
  params.set("status", filters.status || "Select");
  return params;
};

export default function VehicleBreakdownReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingMeta || loadingList;
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    vehicleBasis: "OWN",
    month: "Select",
    year: "Select",
    branchId: toStr(sessionBranchId) || "Select",
    regNo: "",
    vehicleCode: "",
    vehicleName: "",
    fromDate: "",
    toDate: "",
    vehicleTypeId: "Select",
    status: "Select",
  });
  const [rows, setRows] = useState([]);

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const loadMeta = async () => {
    try {
      setLoadingMeta(true);
      const [bRes, tRes] = await Promise.all([
        axios.get(`${API_BASE}/branches`, { headers: apiHeaders }),
        axios.get(`${API_BASE}/vehicleTypes`, { headers: apiHeaders }),
      ]);
      setBranches(bRes.data?.rows || []);
      setVehicleTypes(tRes.data?.rows || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load dropdowns");
    } finally {
      setLoadingMeta(false);
    }
  };

  const buildListUrl = (f) => {
    const params = new URLSearchParams();
    params.set("vehicleBasis", toStr(f.vehicleBasis || "OWN"));
    params.set("month", isSelect(f.month) ? "Select" : toStr(f.month));
    params.set("year", isSelect(f.year) ? "Select" : toStr(f.year));
    params.set("branchId", isSelect(f.branchId) ? "Select" : toStr(f.branchId));
    params.set("regNo", toStr(f.regNo));
    params.set("vehicleCode", toStr(f.vehicleCode));
    params.set("vehicleName", toStr(f.vehicleName));
    params.set("fromDate", toStr(f.fromDate));
    params.set("toDate", toStr(f.toDate));
    params.set("vehicleTypeId", isSelect(f.vehicleTypeId) ? "Select" : toStr(f.vehicleTypeId));
    params.set("status", isSelect(f.status) ? "Select" : toStr(f.status));
    return `${API_BASE}/list?${params.toString()}`;
  };

  const loadList = async (f, silent = false) => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(buildListUrl(f), { headers: apiHeaders });
      if (!data?.success) throw new Error(data?.message || "Failed to load list");
      setRows(data.rows || []);
      setCurrentPage(1);
      if (!silent) toast.success(`Found ${data?.count ?? (data.rows || []).length} records`);
    } catch (err) {
      setRows([]);
      toast.error(err?.message || "List fetch failed");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  const handleSearch = () => loadList(filters);
  const handleViewAll = () => {
    const reset = {
      vehicleBasis: filters.vehicleBasis || "OWN",
      month: "Select", year: "Select", branchId: "Select",
      regNo: "", vehicleCode: "", vehicleName: "",
      fromDate: "", toDate: "", vehicleTypeId: "Select", status: "Select",
    };
    setFilters(reset);
    loadList(reset, true);
    toast.info("Showing all records");
  };
  const handleClear = () => {
    setFilters((p) => ({
      ...p, month: "Select", year: "Select", branchId: sessionBranchType === "HO" ? "Select" : toStr(sessionBranchId) || "Select",
      regNo: "", vehicleCode: "", vehicleName: "", fromDate: "", toDate: "", vehicleTypeId: "Select", status: "Select",
    }));
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const handlePrintAll = () => navigate(`/vehicle/VehicleBreakdownReport_Print?${buildFilterParams(filters).toString()}`);
  const handleOpenDetailedReport = () => navigate(`/vehicle/VehicleBreakdownDetailsReport?${buildFilterParams(filters).toString()}`);

  const handleOpenProgressStatus = (rawRow) => {
    const vehicleId = rawRow?.vehicleId ?? rawRow?.VehicleId ?? rawRow?.fld_vehicleid ?? rawRow?.fld_vehicletypeid ?? rawRow?.id ?? 0;
    const rowBranchId = rawRow?.branchId ?? rawRow?.BranchId ?? rawRow?.fld_branchid ?? rawRow?.fld_branch_id ?? filters.branchId ?? sessionBranchId ?? 0;
    const status = rawRow?.fld_status ?? rawRow?.Status ?? rawRow?.status ?? "";
    if (!vehicleId || !rowBranchId) return toast.error("vehicleId/branchId missing in row.");
    const params = new URLSearchParams();
    params.set("vehicleId", String(vehicleId));
    params.set("branchId", String(rowBranchId));
    params.set("status", String(status || ""));
    navigate(`/vehicle/ProgressStatus?${params.toString()}`);
  };

  const yearOptions = useMemo(() => buildYearOptions(), []);
  const branchOptions = useMemo(
    () =>
      (branches || [])
        .map((b) => ({
          value: toStr(b.fld_branchid ?? b.branchid ?? b.BranchId ?? b.id ?? b.value),
          label: toStr(b.fld_BranchName ?? b.fld_branchname ?? b.branchname ?? b.BranchName ?? b.label),
        }))
        .filter((o) => o.value || o.label),
    [branches]
  );
  const vehicleTypeOptions = useMemo(
    () =>
      (vehicleTypes || [])
        .map((v) => ({
          value: toStr(v.fld_vehiclemaintypeid ?? v.fld_vehicletypeid ?? v.vehicleTypeId ?? v.id ?? v.value),
          label: toStr(v.fld_vehiclemaintypename ?? v.fld_vehicletypename ?? v.fld_vehicletypeindex ?? v.VehicleTypeName ?? v.vehicleTypeName ?? v.label),
        }))
        .filter((o) => o.label),
    [vehicleTypes]
  );

  const normRows = useMemo(
    () =>
      (rows || []).map((r, idx) => {
        const p = (...keys) => {
          for (const k of keys) {
            const v = r?.[k];
            if (v !== undefined && v !== null && String(v).trim() !== "") return v;
          }
          return "";
        };
        return {
          sno: r.sno ?? idx + 1,
          branch: p("fld_branchname", "Branch", "branch"),
          vehicleCode: p("fld_vehicle_code", "VehicleCode", "vehicleCode", "code"),
          vehicleRegno: p("fld_vehicle_regno", "VehicleRegNo", "regNo"),
          status: p("fld_status", "Status", "status"),
          fromDate: p("fld_fromdate", "FromDate", "fromDate"),
          toDate: p("fld_todate", "ToDate", "toDate"),
          breakdownDays: p("fld_breakdowndays", "BreakdownDays", "breakdownDays"),
          _row: r,
        };
      }),
    [rows]
  );

  const totalPages = Math.max(1, Math.ceil(normRows.length / itemsPerPage));
  const pagedRows = normRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">VEHICLE IDLE / BREAKDOWN REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Vehicle Idle / Breakdown Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintAll} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={() => exportToCsv(normRows)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExport /> Export</button>
              <button onClick={loadMeta} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <button type="button" onClick={handleOpenDetailedReport} className={`underline font-semibold ${isDark ? "text-blue-200 hover:text-blue-100" : "text-blue-700 hover:text-blue-900"}`}>
              Detailed Report
            </button>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClear} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="vehicleBasis" checked={filters.vehicleBasis === "OWN"} onChange={() => setFilters((p) => ({ ...p, vehicleBasis: "OWN" }))} />
                  <span className={isDark ? "text-gray-200" : "text-gray-800"}>Own Vehicle</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="vehicleBasis" checked={filters.vehicleBasis === "HIRE"} onChange={() => setFilters((p) => ({ ...p, vehicleBasis: "HIRE" }))} />
                  <span className={isDark ? "text-gray-200" : "text-gray-800"}>Hire Vehicle</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0"><label className={labelClass}>Month</label><select name="month" value={filters.month} onChange={handleChange} className={fieldClass}>{monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Year</label><select name="year" value={filters.year} onChange={handleChange} className={fieldClass}>{yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Branch</label><select name="branchId" value={filters.branchId} onChange={handleChange} className={fieldClass}><option value="Select">Select</option>{branchOptions.map((b) => <option key={`${b.value}-${b.label}`} value={b.value}>{b.label || b.value}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Code</label><input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Vehicle Code" /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Registration Number</label><input type="text" name="regNo" value={filters.regNo} onChange={handleChange} className={fieldClass} placeholder="Vehicle Reg.No" /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Name</label><input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Vehicle Name" /></div>
                <div className="min-w-0"><label className={labelClass}>From Date</label><input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} /></div>
                <div className="min-w-0"><label className={labelClass}>To Date</label><input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Main Type</label><select name="vehicleTypeId" value={filters.vehicleTypeId} onChange={handleChange} className={fieldClass}><option value="Select">Select</option>{vehicleTypeOptions.map((v) => <option key={`${v.value}-${v.label}`} value={v.value}>{v.label}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Status</label><select name="status" value={filters.status} onChange={handleChange} className={fieldClass}><option value="Select">Select</option><option value="Running">Running</option><option value="Idle">Idle</option><option value="Breakdown">Breakdown</option></select></div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {normRows.length}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingMeta ? "Loading dropdowns..." : loadingList ? "Searching..." : "INFRA SCREEN : B-57"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : pagedRows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div></div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Branch</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Code</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Reg No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Status</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">From</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">To</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Days</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, idx) => (
                    <tr key={`${r.vehicleCode || "row"}-${idx}`} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.branch}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.vehicleCode}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.vehicleRegno}</td>
                      <td className="px-3 py-3 text-sm whitespace-nowrap">
                        <button type="button" onClick={() => handleOpenProgressStatus(r._row)} className={`px-3 py-1 rounded text-xs font-semibold ${isDark ? "bg-blue-900 text-blue-100 hover:bg-blue-800" : "bg-blue-100 text-blue-800 hover:bg-blue-200"}`}>
                          {r.status || "View"}
                        </button>
                      </td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fromDate}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.toDate}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.breakdownDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {normRows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, normRows.length)} of {normRows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}